// Clipboard + Markdown export helpers shared across the app.
import { toast } from "sonner";

const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

/** Format ISO-ish date strings (YYYY-MM-DD / YYYY-MM / YYYY) into friendly display. */
function fmtDate(s) {
  if (!s) return "?";
  const m = String(s).trim().match(/^(\d{4})(?:-(\d{1,2}))?(?:-(\d{1,2}))?$/);
  if (!m) return String(s);
  const [, y, mo, d] = m;
  if (d) return `${parseInt(d, 10)} ${MONTHS_SHORT[parseInt(mo, 10) - 1] || ""} ${y}`.trim();
  if (mo) return `${MONTHS_SHORT[parseInt(mo, 10) - 1] || ""} ${y}`.trim();
  return y;
}

/** Copy text to clipboard with graceful fallback + reliable user feedback. */
export async function copyText(text, successLabel = "Copied") {
  if (!text) return false;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      toast.success(successLabel);
      return true;
    }
    throw new Error("Clipboard API unavailable");
  } catch (err) {
    // Legacy fallback for older browsers / non-secure contexts
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (ok) {
        toast.success(successLabel);
        return true;
      }
    } catch (e) {
      console.warn("execCommand copy fallback failed:", e?.message || e);
    }
    toast.error("Couldn't copy — long-press to select and copy manually");
    return false;
  }
}

/** Markdown formatters per resource type. Always concise CV-style output. */
export const asMarkdown = {
  profile(pi = {}) {
    const lines = [];
    if (pi.full_name) lines.push(`# ${pi.full_name}`);
    if (pi.headline) lines.push(`_${pi.headline}_`);
    const contact = [pi.email, pi.phone, pi.location].filter(Boolean).join(" · ");
    if (contact) lines.push(contact);
    const links = [
      pi.linkedin && `[LinkedIn](${pi.linkedin})`,
      pi.website && `[Website](${pi.website})`,
      pi.portfolio && `[Portfolio](${pi.portfolio})`,
    ].filter(Boolean).join(" · ");
    if (links) lines.push(links);
    if (pi.summary) { lines.push(""); lines.push(pi.summary); }
    return lines.join("\n");
  },

  work(w = {}) {
    const dates = `${fmtDate(w.start_date)} — ${w.current ? "Present" : fmtDate(w.end_date)}`;
    const head = `**${w.role || ""}** · ${w.employer || ""}${w.location ? ` · ${w.location}` : ""}`;
    const meta = `_${dates}${w.kind && w.kind !== "job" ? ` · ${w.kind}` : ""}_`;
    const lines = [head, meta];
    if (w.responsibilities) {
      lines.push("");
      // Convert "• …" or newlines into proper md bullets
      const bullets = w.responsibilities
        .split(/\n+/).map((l) => l.replace(/^[•\-*]\s*/, "").trim()).filter(Boolean);
      bullets.forEach((b) => lines.push(`- ${b}`));
    }
    if (w.skills) lines.push(`\n**Skills:** ${w.skills}`);
    return lines.join("\n");
  },

  education(e = {}) {
    const dates = `${fmtDate(e.start_date)} — ${fmtDate(e.end_date)}`;
    const head = `**${e.qualification || ""}** · ${e.provider || ""}`;
    const meta = [
      dates,
      e.kind && e.kind !== "degree" ? e.kind : null,
      e.level || null,
      e.grade ? `Grade: ${e.grade}` : null,
      e.awarding_body || null,
    ].filter(Boolean).join(" · ");
    return [head, `_${meta}_`].join("\n");
  },

  reference(r = {}) {
    const head = `**${r.name}**${r.role ? ` — ${r.role}` : ""}${r.company ? `, ${r.company}` : ""}`;
    const contact = [r.email, r.phone].filter(Boolean).join(" · ");
    const lines = [head];
    if (contact) lines.push(`_${contact}_`);
    if (r.relationship) lines.push(`_${r.relationship}_`);
    if (r.notes) lines.push(r.notes);
    return lines.join("\n");
  },

  skills(items = []) {
    return items.map((s) => s.level ? `${s.name} _(${s.level})_` : s.name).join(", ");
  },

  customItem(it = {}) {
    const head = `**${it.title || ""}**${it.subtitle ? ` — ${it.subtitle}` : ""}${it.date ? ` _(${it.date})_` : ""}`;
    const lines = [head];
    if (it.url) lines.push(it.url);
    if (it.body) { lines.push(""); lines.push(it.body); }
    return lines.join("\n");
  },

  /** Build the full vault as a single Markdown document. */
  fullVault(data = {}) {
    const out = [];
    out.push(asMarkdown.profile(data.personal_info));

    if (data.work_experience?.length) {
      out.push("\n## Work Experience\n");
      out.push(data.work_experience.map(asMarkdown.work).join("\n\n"));
    }
    if (data.education?.length) {
      out.push("\n## Education\n");
      out.push(data.education.map(asMarkdown.education).join("\n\n"));
    }
    if (data.skills?.length) {
      out.push("\n## Skills\n");
      out.push(asMarkdown.skills(data.skills));
    }
    if (data.references?.length) {
      out.push("\n## References\n");
      out.push(data.references.map(asMarkdown.reference).join("\n\n"));
    }
    (data.custom_sections || []).forEach((sec) => {
      if (sec.items?.length) {
        out.push(`\n## ${sec.name}\n`);
        out.push(sec.items.map(asMarkdown.customItem).join("\n\n"));
      }
    });
    return out.join("\n");
  },
};

/** LinkedIn-flavoured plain text — no Markdown asterisks, real bullets, char-count helpers.
 *  LinkedIn limits: Headline 220, About 2600, Experience description 2000.
 */
const LI_LIMITS = { headline: 220, about: 2600, role: 2000 };

function _bullets(raw) {
  if (!raw) return [];
  return raw.split(/\n+/).map((l) => l.replace(/^[•\-*]\s*/, "").trim()).filter(Boolean);
}

export const asLinkedIn = {
  headline(pi = {}) {
    return (pi.headline || "").slice(0, LI_LIMITS.headline);
  },

  about(pi = {}) {
    // LinkedIn "About" section — plain text, double newlines between paragraphs.
    const parts = [];
    if (pi.summary) parts.push(pi.summary);
    const contact = [pi.email, pi.phone, pi.location].filter(Boolean).join(" · ");
    if (contact) parts.push(contact);
    const links = [pi.linkedin, pi.website, pi.portfolio].filter(Boolean).join("\n");
    if (links) parts.push(links);
    return parts.join("\n\n").slice(0, LI_LIMITS.about);
  },

  workRole(w = {}) {
    // For pasting into a single LinkedIn Experience "description" field.
    const lines = [];
    const bullets = _bullets(w.responsibilities);
    bullets.forEach((b) => lines.push(`• ${b}`));
    if (w.skills) lines.push(`\nSkills: ${w.skills}`);
    return lines.join("\n").slice(0, LI_LIMITS.role);
  },

  /** Full vault as LinkedIn-paste-friendly plain text (for "All-in-one" copy). */
  fullVault(data = {}) {
    const pi = data.personal_info || {};
    const out = [];

    if (pi.full_name) out.push(pi.full_name.toUpperCase());
    if (pi.headline) out.push(pi.headline);
    const contact = [pi.email, pi.phone, pi.location].filter(Boolean).join(" · ");
    if (contact) out.push(contact);
    const links = [pi.linkedin, pi.website, pi.portfolio].filter(Boolean).join(" · ");
    if (links) out.push(links);
    if (pi.summary) { out.push(""); out.push("ABOUT"); out.push(pi.summary); }

    if (data.work_experience?.length) {
      out.push(""); out.push("EXPERIENCE");
      data.work_experience.forEach((w) => {
        out.push("");
        const dates = `${fmtDate(w.start_date)} — ${w.current ? "Present" : fmtDate(w.end_date)}`;
        out.push(`${w.role || ""} — ${w.employer || ""}`);
        out.push(`${dates}${w.location ? ` · ${w.location}` : ""}${w.kind && w.kind !== "job" ? ` · ${w.kind}` : ""}`);
        _bullets(w.responsibilities).forEach((b) => out.push(`• ${b}`));
        if (w.skills) out.push(`Skills: ${w.skills}`);
      });
    }

    if (data.education?.length) {
      out.push(""); out.push("EDUCATION");
      data.education.forEach((e) => {
        out.push("");
        out.push(`${e.qualification || ""} — ${e.provider || ""}`);
        const meta = [
          `${fmtDate(e.start_date)} — ${fmtDate(e.end_date)}`,
          e.kind && e.kind !== "degree" ? e.kind : null,
          e.level || null,
          e.grade ? `Grade: ${e.grade}` : null,
        ].filter(Boolean).join(" · ");
        if (meta) out.push(meta);
      });
    }

    if (data.skills?.length) {
      out.push(""); out.push("SKILLS");
      out.push(data.skills.map((s) => s.name).filter(Boolean).join(", "));
    }

    return out.join("\n");
  },
};

/** Convenience: copy a single entry as Markdown. */
export function copyAsMarkdown(formatter, item, label = "Copied as Markdown") {
  return copyText(formatter(item), label);
}
